import { useEffect, useRef, useCallback, useState } from 'react'
import { wsService, type EventType } from '@/services/websocket'
import { useAuth } from '@/contexts/AuthContext'

interface UseWebSocketOptions {
  autoConnect?: boolean
  events?: {
    type: EventType
    handler: (data: any) => void
  }[]
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { user } = useAuth()
  const [isConnected, setIsConnected] = useState(false)
  const [connectionError, setConnectionError] = useState<string | null>(null)
  const unsubscribers = useRef<(() => void)[]>([])
  
  // Connect to WebSocket
  const connect = useCallback(async () => {
    if (!user) {
      setConnectionError('Not authenticated')
      return
    }
    
    try {
      await wsService.connect()
      setIsConnected(true)
      setConnectionError(null)
    } catch (error) {
      console.error('WebSocket connection failed:', error)
      setConnectionError('Failed to connect to real-time updates')
      setIsConnected(false)
    }
  }, [user])
  
  // Disconnect from WebSocket
  const disconnect = useCallback(() => {
    wsService.disconnect()
    setIsConnected(false)
  }, [])
  
  // Subscribe to events
  const subscribe = useCallback((
    event: EventType,
    handler: (data: any) => void
  ): (() => void) => {
    const unsubscribe = wsService.on(event, handler)
    unsubscribers.current.push(unsubscribe)
    return unsubscribe
  }, [])
  
  // Send message
  const send = useCallback((type: EventType, data: any) => {
    wsService.send(type, data)
  }, [])
  
  // Auto-connect and setup event listeners
  useEffect(() => {
    if (options.autoConnect !== false && user) {
      connect()
    }
    
    // Subscribe to connection events
    const unsubConnected = wsService.on('connected', () => {
      setIsConnected(true)
      setConnectionError(null)
    })
    
    const unsubDisconnected = wsService.on('disconnected', () => {
      setIsConnected(false)
    })
    
    const unsubError = wsService.on('error', (error: any) => {
      setConnectionError(error.message || 'Connection error')
    })
    
    // Subscribe to custom events
    if (options.events) {
      options.events.forEach(({ type, handler }) => {
        const unsub = wsService.on(type, handler)
        unsubscribers.current.push(unsub)
      })
    }
    
    // Cleanup
    return () => {
      unsubscribers.current.forEach(unsub => unsub())
      unsubscribers.current = []
      unsubConnected()
      unsubDisconnected()
      unsubError()
      
      if (options.autoConnect !== false) {
        disconnect()
      }
    }
  }, [user, options.autoConnect, connect, disconnect])
  
  // Update connection status
  useEffect(() => {
    const checkConnection = () => {
      setIsConnected(wsService.isConnected)
    }
    
    const interval = setInterval(checkConnection, 1000)
    return () => clearInterval(interval)
  }, [])
  
  return {
    isConnected,
    connectionError,
    connect,
    disconnect,
    subscribe,
    send
  }
}